import { type FormEvent, useState } from 'react'
import {
  Alert,
  Button,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  TextInput,
} from 'flowbite-react'
import { useAuthSession, useAuthStore } from '@stores/authStore'
import { initialPasswordChangeValues } from './constants'
import { flattenPasswordChangeErrors } from './helpers'

interface PasswordChangeModalProps {
  open: boolean
  onClose: () => void
}

function PasswordChangeModal({ open, onClose }: PasswordChangeModalProps) {
  const session = useAuthSession()
  const changePassword = useAuthStore((state) => state.changePassword)
  const [values, setValues] = useState(initialPasswordChangeValues)
  const [errors, setErrors] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const needsOldPassword = session?.user?.needsOldPassword ?? true

  function setValue(field: keyof typeof initialPasswordChangeValues, value: string) {
    setValues((current) => ({
      ...current,
      [field]: value,
    }))
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!session) {
      return
    }

    setErrors([])
    setIsSubmitting(true)
    void changePassword(values, session.csrfToken)
      .then((result) => {
        if (result.success) {
          onClose()
          return
        }

        setErrors(flattenPasswordChangeErrors(result.errors))
      })
      .catch((error) => {
        setErrors([
          error instanceof Error
            ? error.message
            : 'Не удалось изменить пароль',
        ])
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  return (
    <Modal onClose={onClose} popup show={open} size="md">
      <ModalHeader className="m-4">Смена пароля</ModalHeader>
      <ModalBody>
        <form className="grid gap-4" onSubmit={handleSubmit}>
          {errors.length ? (
            <Alert color="failure">
              <ul className="m-0 list-disc pl-4">
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </Alert>
          ) : null}

          {needsOldPassword ? (
            <Label className="grid gap-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
              <span>Старый пароль</span>
              <TextInput
                autoComplete="current-password"
                onChange={(event) => setValue('oldPassword', event.target.value)}
                required
                type="password"
                value={values.oldPassword}
              />
            </Label>
          ) : null}

          <Label className="grid gap-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
            <span>Новый пароль</span>
            <TextInput
              autoComplete="new-password"
              onChange={(event) => setValue('newPassword1', event.target.value)}
              required
              type="password"
              value={values.newPassword1}
            />
          </Label>

          <Label className="grid gap-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
            <span>Подтверждение пароля</span>
            <TextInput
              autoComplete="new-password"
              onChange={(event) => setValue('newPassword2', event.target.value)}
              required
              type="password"
              value={values.newPassword2}
            />
          </Label>

          <div className="flex justify-end gap-3 border-t border-slate-200 pt-3 dark:border-zinc-800">
            <Button
              color="light"
              disabled={isSubmitting}
              onClick={onClose}
              type="button"
            >
              Отмена
            </Button>
            <Button disabled={isSubmitting || !session} type="submit">
              {isSubmitting ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  )
}

export default PasswordChangeModal
